import { exportFilenameSlug } from './exportFiles'

export function selectCandidateCv(attachments) {
	return attachments.reduce((newest, attachment) => {
		const flags = attachment.custom_properties || {}
		if (!flags.is_cv_upload && !flags.is_shared_cv) return newest
		return !newest || Number(attachment.id) > Number(newest.id)
			? attachment
			: newest
	}, null)
}

export function buildCandidateZipFilename(candidateName, jobTitle) {
	return `${exportFilenameSlug(candidateName) ||
		'candidate'}-${exportFilenameSlug(jobTitle) || 'job'}.zip`
}

function uniqueFilename(attachment, entries) {
	const name = String(attachment.file_name || attachment.name || 'attachment')
		.split(/[\\/]/)
		.pop()
		.replace(/[\x00-\x1f\x7f]/g, '')
		.trim()
	const safeName =
		!name || /^\.+$/.test(name)
			? 'attachment'
			: ['__proto__', 'constructor', 'prototype'].includes(name)
			? `attachment-${name}`
			: name
	const dot = safeName.lastIndexOf('.')
	const stem = dot > 0 ? safeName.slice(0, dot) : safeName
	const extension = dot > 0 ? safeName.slice(dot) : ''
	let filename = safeName
	let suffix = 2
	while (Object.prototype.hasOwnProperty.call(entries, filename)) {
		filename = `${stem} (${suffix++})${extension}`
	}
	return filename
}

const DOWNLOAD_CHUNK_BYTES = 2 * 1024 * 1024

async function fetchAttachment(axios, url, jobOfferId, isCurrent) {
	const chunks = []
	let offset = 0
	let part
	do {
		if (!isCurrent()) return null
		const response = await axios.get(url, {
			params: {
				job_offer_id: jobOfferId,
				offset,
				length: DOWNLOAD_CHUNK_BYTES
			},
			responseType: 'arraybuffer',
			__skipGlobalErrorToast: true
		})
		if (!isCurrent()) return null
		part = new Uint8Array(response.data)
		if (part.length > DOWNLOAD_CHUNK_BYTES)
			throw new Error(
				'Attachment response exceeds the requested byte range'
			)
		chunks.push(part)
		offset += part.length
	} while (part.length === DOWNLOAD_CHUNK_BYTES)

	const bytes = new Uint8Array(offset)
	let position = 0
	for (const chunk of chunks) {
		bytes.set(chunk, position)
		position += chunk.length
	}
	return bytes
}

export async function buildCandidateZip({
	selection,
	attachments,
	renderExportImage,
	axios,
	applicantProfileId,
	jobOfferId,
	isCurrent = () => true
}) {
	const files =
		selection === 'all'
			? attachments
			: selection === 'cv'
			? [selectCandidateCv(attachments)].filter(Boolean)
			: []
	if (!files.length) throw new Error('No candidate attachments selected')
	if (!isCurrent()) return null
	const image = await renderExportImage()
	if (!isCurrent()) return null
	const { zipSync } = await import('fflate')
	if (!isCurrent()) return null
	const entries = Object.create(null)
	entries['personality-profile.png'] = new Uint8Array(
		await image.arrayBuffer()
	)
	for (const attachment of files) {
		const bytes = await fetchAttachment(
			axios,
			`/applicant-profile/${applicantProfileId}/attachment/${attachment.id}`,
			jobOfferId,
			isCurrent
		)
		if (!bytes) return null
		entries[uniqueFilename(attachment, entries)] = bytes
	}
	if (!isCurrent()) return null
	return new Blob([zipSync(entries)], { type: 'application/zip' })
}
